import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, Skeleton } from '@rackd/ui';
import { IconSearch, IconUsers, IconCrown, IconClock } from '@tabler/icons-react';
import { useUsers, UserCard } from '@/features/users';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const }
  },
};

type TierFilter = 'all' | 'free' | 'power_rack' | 'trial';

const tierTabs: { value: TierFilter; label: string; icon: typeof IconUsers }[] = [
  { value: 'all', label: 'All Users', icon: IconUsers },
  { value: 'power_rack', label: 'Power Rack', icon: IconCrown },
  { value: 'trial', label: 'Trial', icon: IconClock },
  { value: 'free', label: 'Free', icon: IconUsers },
];

export function UsersPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState<TierFilter>('all');

  const { users, total, isLoading, error } = useUsers({
    query: searchQuery,
    tier: tierFilter === 'all' ? undefined : tierFilter,
  });

  return (
    <motion.div
      className="space-y-6"
      variants={pageVariants}
      initial="initial"
      animate="animate"
    >
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--text)]">Users</h1>
        <p className="text-sm text-[var(--text-tertiary)] mt-1">
          Search and manage user accounts ({total} total)
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <IconSearch
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]"
        />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by email or name..."
          className="w-full pl-10 pr-4 py-2.5 bg-[var(--input-bg)] border border-[var(--border)] rounded-lg text-[var(--text)] placeholder:text-[var(--text-faint)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] focus:border-transparent"
        />
      </div>

      {/* Tier filter tabs */}
      <div className="flex items-center gap-2 border-b border-[var(--border)] pb-4">
        {tierTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = tierFilter === tab.value;
          return (
            <button
              key={tab.value}
              onClick={() => setTierFilter(tab.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-[var(--accent-primary)] text-white'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--background-tertiary)]'
              }`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i} className="p-4">
              <div className="flex items-center gap-4">
                <Skeleton className="w-12 h-12 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-4 w-56" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="p-8 text-center">
          <p className="text-[var(--error)]">Error loading users</p>
          <p className="text-sm text-[var(--text-tertiary)] mt-1">{error.message}</p>
        </Card>
      ) : users.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="w-16 h-16 bg-[var(--background-tertiary)] rounded-full flex items-center justify-center mx-auto mb-4">
            <IconUsers size={32} className="text-[var(--text-tertiary)]" />
          </div>
          <h3 className="text-lg font-semibold text-[var(--text)] mb-1">No users found</h3>
          <p className="text-sm text-[var(--text-tertiary)]">
            {searchQuery
              ? `No users match "${searchQuery}"`
              : 'No users registered yet.'}
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {users.map((user) => (
            <UserCard key={user.id} user={user} />
          ))}
        </div>
      )}
    </motion.div>
  );
}
